import { isEqual } from "@common/helpers/comparison";
import { ViewType } from "@common/types/views/views";
import {
    BillView,
    BillsByVendorView,
    BillColumn,
    BillsByVendorColumn,
    BillLineItemView,
    BillLineItemColumn,
} from "@common/types/views/billViews";

export const defaultBillsView: BillView = {
    type: ViewType.BILL,
    columns: [
        { id: BillColumn.LOCK, options: { pin: "left" } },
        { id: BillColumn.DATE, options: { pin: "left" } },
        { id: BillColumn.EXTERNAL_STATUS },
        { id: BillColumn.INVOICE_NUMBER },
        { id: BillColumn.VENDOR },
        { id: BillColumn.ACCOUNT },
        { id: BillColumn.DUE_DATE },
        { id: BillColumn.AMOUNT },
        { id: BillColumn.MATCH },
        { id: BillColumn.ENTITY, options: { pin: "right" } },
        { id: BillColumn.EXTERNAL_LINK, options: { pin: "right" } },
    ],
    filters: {},
    options: {
        selection: true,
        bulkActions: true,
    },
};

export const createDefaultBillLineItemsView = ({
    hasAnyAccrualLedgers,
}: {
    hasAnyAccrualLedgers: boolean;
}): BillLineItemView => ({
    type: ViewType.BILL_LINE_ITEM,
    columns: [
        { id: BillLineItemColumn.LOCK, options: { pin: "left" } },
        { id: BillLineItemColumn.DATE, options: { pin: "left" } },
        { id: BillLineItemColumn.EXTERNAL_STATUS },
        { id: BillLineItemColumn.VENDOR },
        { id: BillLineItemColumn.DESCRIPTION },
        { id: BillLineItemColumn.ACCRUAL_ACCOUNT },
        ...(hasAnyAccrualLedgers
            ? [{ id: BillLineItemColumn.ACCOUNTS_PAYABLE as const }]
            : []),
        { id: BillLineItemColumn.DUE_DATE },
        { id: BillLineItemColumn.ACCOUNT },
        { id: BillLineItemColumn.AMOUNT },
        { id: BillLineItemColumn.ENTITY, options: { pin: "right" } },
        { id: BillLineItemColumn.EXTERNAL_LINK, options: { pin: "right" } },
    ],
    filters: {},
    options: {
        selection: true,
        bulkActions: true,
    },
});

export const defaultBillLineItemsView: BillLineItemView =
    createDefaultBillLineItemsView({ hasAnyAccrualLedgers: false });

export const addPayablesToDefaultBillLineItemsView = (
    view: BillLineItemView,
): BillLineItemView => {
    const hasPayablesColumn = view.columns.some(
        (column) => column.id === BillLineItemColumn.ACCOUNTS_PAYABLE,
    );
    if (
        hasPayablesColumn ||
        !isEqual(view.columns, defaultBillLineItemsView.columns)
    ) {
        return view;
    }

    return {
        ...view,
        columns: createDefaultBillLineItemsView({
            hasAnyAccrualLedgers: true,
        }).columns,
    };
};

export const defaultBillsByVendorView: BillsByVendorView = {
    type: ViewType.BILLS_BY_VENDOR,
    columns: [
        { id: BillsByVendorColumn.VENDOR },
        { id: BillsByVendorColumn.DUE_1_TO_30 },
        { id: BillsByVendorColumn.DUE_31_TO_60 },
        { id: BillsByVendorColumn.DUE_61_PLUS },
        { id: BillsByVendorColumn.OVERDUE },
        { id: BillsByVendorColumn.TOTAL_OPEN, options: { pin: "right" } },
        { id: BillsByVendorColumn.TOTAL_PAID, options: { pin: "right" } },
        {
            id: BillsByVendorColumn.BILLS_COUNT,
            options: { pin: "right" },
        },
        { id: BillsByVendorColumn.ENTITIES, options: { pin: "right" } },
    ],
    filters: {},
    options: {
        selection: false,
    },
};
